/**
 * Stav proplacení / fakturace řádku výkazu práce (`work_report.payment_status`).
 * Výchozí je neproplaceno. Uživatel/admin vytvoří fakturu (vyfakturované, čeká na platbu);
 * admin po „Proplaceno“ přepne na proplacené.
 */

export const WORK_REPORT_PAYMENT_STATUS = {
  UNPAID: 'unpaid',
  /** Vyfakturované — řádek je ve faktuře čekající na admin „Proplaceno“. */
  INVOICED: 'invoiced',
  /** Proplacené — finální stav po admin platbě. */
  PAID: 'paid',
} as const;

export type WorkReportPaymentStatus = (typeof WORK_REPORT_PAYMENT_STATUS)[keyof typeof WORK_REPORT_PAYMENT_STATUS];

export const WORK_REPORT_PAYMENT_STATUS_LABELS: Record<WorkReportPaymentStatus, string> = {
  [WORK_REPORT_PAYMENT_STATUS.UNPAID]: 'Neproplacené',
  [WORK_REPORT_PAYMENT_STATUS.INVOICED]: 'Vyfakturované',
  [WORK_REPORT_PAYMENT_STATUS.PAID]: 'Proplacené',
};

/** Stav faktury (skupina řádků se stejným `invoice_id`). */
export const WORK_REPORT_INVOICE_STATUS = {
  /** Čeká na admin platbu — alespoň jeden řádek je stále `invoiced`. */
  PENDING: 'pending',
  /** Admin označil jako proplaceno — řádky jsou `paid`. */
  PAID: 'paid',
} as const;

export type WorkReportInvoiceStatus =
  (typeof WORK_REPORT_INVOICE_STATUS)[keyof typeof WORK_REPORT_INVOICE_STATUS];

/** Filtr historických faktur pro admin přehled Fakturace (`paid` | `unpaid`/`pending` | `all`). */
export type WorkReportInvoiceStatusFilter = WorkReportInvoiceStatus | 'all';

/**
 * Query `status` pro GET …/work-report/invoices.
 * `unpaid` je alias pro `pending` (neproplacené faktury).
 * Neznámá / prázdná hodnota → `all`.
 */
export function parseWorkReportInvoiceStatusFilter(
  value: string | null | undefined,
): WorkReportInvoiceStatusFilter {
  const v = (value ?? '').trim().toLowerCase();
  if (v === WORK_REPORT_INVOICE_STATUS.PAID) return WORK_REPORT_INVOICE_STATUS.PAID;
  if (v === WORK_REPORT_INVOICE_STATUS.PENDING || v === 'unpaid') {
    return WORK_REPORT_INVOICE_STATUS.PENDING;
  }
  return 'all';
}

/** Normalizace z API (null / neznámá → unpaid). */
export function normalizeWorkReportPaymentStatus(value: string | null | undefined): WorkReportPaymentStatus {
  const v = (value ?? '').trim().toLowerCase();
  if (v === WORK_REPORT_PAYMENT_STATUS.PAID) return WORK_REPORT_PAYMENT_STATUS.PAID;
  if (v === WORK_REPORT_PAYMENT_STATUS.INVOICED) return WORK_REPORT_PAYMENT_STATUS.INVOICED;
  return WORK_REPORT_PAYMENT_STATUS.UNPAID;
}

/** Běžný uživatel nesmí měnit řádek ve stavu vyfakturované / proplacené. */
export function isWorkReportPaymentLockedForUser(
  paymentStatus: string | null | undefined,
  isAdmin: boolean,
): boolean {
  if (isAdmin) return false;
  return isWorkReportBillingImmutable(paymentStatus);
}

/**
 * Řádek ve faktuře (`invoiced` / `paid`) se nesmí mazat ani mizet při smazání/úpravě objektu
 * (hard-delete, návrat fáze, milník revert) — stejně pro admin i běžného uživatele.
 * Admin smí dál měnit obsah přes PATCH a řídit fakturační workflow (pay / revert / unbill).
 */
export function isWorkReportBillingImmutable(paymentStatus: string | null | undefined): boolean {
  const s = normalizeWorkReportPaymentStatus(paymentStatus);
  return s === WORK_REPORT_PAYMENT_STATUS.INVOICED || s === WORK_REPORT_PAYMENT_STATUS.PAID;
}

/** Možnosti filtru proplacení (checkbox multi-select ve výkazu práce). */
export const WORK_REPORT_PAYMENT_FILTER_OPTIONS: ReadonlyArray<{ value: WorkReportPaymentStatus; label: string }> = [
  { value: WORK_REPORT_PAYMENT_STATUS.UNPAID, label: WORK_REPORT_PAYMENT_STATUS_LABELS.unpaid },
  { value: WORK_REPORT_PAYMENT_STATUS.INVOICED, label: WORK_REPORT_PAYMENT_STATUS_LABELS.invoiced },
  { value: WORK_REPORT_PAYMENT_STATUS.PAID, label: WORK_REPORT_PAYMENT_STATUS_LABELS.paid },
];

/** Výchozí výběr filtru proplacení (jen neproplacené řádky). */
export const WORK_REPORT_PAYMENT_FILTER_DEFAULT_SELECTED: ReadonlyArray<WorkReportPaymentStatus> = [
  WORK_REPORT_PAYMENT_STATUS.UNPAID,
];

/** UI/API kód volné ruční položky „Ostatní“ (není v ceníku). */
export const WORK_REPORT_OTHER_ACTIVITY_CODE = 'OTHER';

/** Ceníková ruční položka (ne Ostatní / prázdný kód). */
export function isWorkReportCatalogManualCode(activityCode?: string | null): boolean {
  const code = (activityCode ?? '').trim();
  return code !== '' && code !== WORK_REPORT_OTHER_ACTIVITY_CODE;
}

/**
 * Stavba (`polygonId`) je povinná jen u ceníkových ručních položek.
 * U Ostatní je volitelná — nevybraná stavba se nesmí dopočítat.
 */
export function isWorkReportPolygonRequiredForManualEntry(activityCode?: string | null): boolean {
  return isWorkReportCatalogManualCode(activityCode);
}

export function normalizeOptionalWorkReportPolygonId(polygonId?: string | null): string | undefined {
  const trimmed = (polygonId ?? '').trim();
  return trimmed || undefined;
}

export {
  applyWorkReportPaymentStatusFilter,
  isWorkReportPaymentFilterDefaultSelected,
  matchesWorkReportPaymentStatusFilter,
  parseWorkReportPaymentStatusCsv,
  resolveWorkReportPaymentStatusServerFilter,
  shouldShowWorkReportPaymentStatusBadge,
} from '../utils/work-report-payment-filter.util';
